import { esc, formatHMS } from './config.js';
import { state, findTask } from './state.js';
import { getRunningEntryAPI, startTimerAPI, stopTimerAPI, loadTasksAPI } from './api.js';

// ══════════════════════════════════════════════════════════
// TIMER — live stopwatch + header indicator pill
// Single responsibility: the running entry (state.runningEntry) and its UI.
// ══════════════════════════════════════════════════════════
let _cb = {};            // { renderSidebar }
let _tick = null;        // setInterval handle (1s)

export function registerTimerCallbacks({ renderSidebar }) {
  _cb = { renderSidebar };
}

// Is the given task the one currently being tracked?
export function isTracking(taskId) {
  return !!state.runningEntry && state.runningEntry.task_id === taskId;
}

// Elapsed ms of the running entry (since its started_at).
function runningElapsedMs() {
  if (!state.runningEntry) return 0;
  return Date.now() - new Date(state.runningEntry.started_at).getTime();
}

// ── Header pill ─────────────────────────────────────────
export function renderTimerIndicator() {
  const host = document.getElementById('timer-indicator');
  if (!host) return;

  if (!state.runningEntry) {
    host.innerHTML = '';
    host.style.display = 'none';
    return;
  }

  const r = state.runningEntry;
  const title = r.task_title || findTask(r.task_id)?.title || 'Tasca';
  host.style.display = '';
  host.innerHTML = `
    <span class="timer-pill" title="Cronòmetre en marxa">
      <span class="timer-pill-dot"></span>
      <span class="timer-pill-title">${esc(title)}</span>
      <span class="timer-pill-time js-live-time" data-base-ms="0">${formatHMS(runningElapsedMs())}</span>
      <button class="timer-pill-stop" id="timer-pill-stop" title="Aturar">■</button>
    </span>
  `;
  host.querySelector('#timer-pill-stop')?.addEventListener('click', () => stopTimer());
}

// Update the ticking numbers (cheap; called every second). Every `.js-live-time` element —
// the header pill and any sidebar task card — carries its own offset in data-base-ms
// (0 for the pill, the closed intervals for a card), so we just add the live session on top.
// Purely DOM-driven, so no import of sidebar.js and no dependency cycle.
function updateTick() {
  const elapsed = runningElapsedMs();
  document.querySelectorAll('.js-live-time').forEach(node => {
    node.textContent = formatHMS((Number(node.dataset.baseMs) || 0) + elapsed);
  });
}

function startTicking() {
  stopTicking();
  _tick = setInterval(updateTick, 1000);
}

function stopTicking() {
  if (_tick) { clearInterval(_tick); _tick = null; }
}

// ── Public actions ──────────────────────────────────────
export async function startTimer(taskId) {
  const entry = await startTimerAPI(taskId);
  if (!entry) return null;
  // The server stamps task_title via the running endpoint; attach what we know.
  state.runningEntry = { ...entry, task_title: findTask(taskId)?.title };
  renderTimerIndicator();
  startTicking();
  // The server closes any other running entry, so that task's total changed too.
  await loadTasksAPI();
  _cb.renderSidebar?.();
  return entry;
}

export async function stopTimer() {
  if (!state.runningEntry) return;
  await stopTimerAPI(state.runningEntry.id);
  state.runningEntry = null;
  stopTicking();
  renderTimerIndicator();
  // The interval just closed now counts towards the task's tracked_seconds.
  await loadTasksAPI();
  _cb.renderSidebar?.();
}

// Restore a running timer after a page reload.
export async function restoreRunningTimer() {
  const entry = await getRunningEntryAPI();
  state.runningEntry = entry || null;
  renderTimerIndicator();
  if (!state.runningEntry) return;
  startTicking();
  // init() renders the sidebar before this resolves, so the cards still show the stopped
  // state — repaint them now that we know which task is running.
  _cb.renderSidebar?.();
}
